import type {
  PlatformProductLength,
  Quantity,
  UnitCode,
} from '~/graphql/graphql.ts';
import { unitsPerPieceOrNull } from '~/utils/unitConversion';
import { getPlatformProductDimensions } from '~/utils/order';

type BundleProductLike = Pick<
  PlatformProductLength['platformProduct'],
  | 'nominalWidthInches'
  | 'actualWidthInches'
  | 'nominalThicknessInches'
  | 'actualThicknessInches'
  | 'actualLengthInches'
  | 'piecesPerBundle'
>;

type BundleLengthLike = Pick<
  PlatformProductLength['length'],
  'nominalLengthInches' | 'actualLengthInches'
>;

export type BundleProductLengthLike = {
  isNominal?: boolean | null;
  platformProduct: BundleProductLike;
  length: BundleLengthLike;
};

export function unitsPerPieceFor(
  platformProductsLength: BundleProductLengthLike,
  unit: UnitCode
): number | null {
  const { widthInches, thicknessInches, lengthInches } =
    getPlatformProductDimensions(
      platformProductsLength.platformProduct,
      platformProductsLength.length,
      platformProductsLength.isNominal
    );
  return unitsPerPieceOrNull(unit, {
    lengthInches,
    widthInches,
    thicknessInches,
  });
}

export function unitQuantityOfPieces(
  platformProductsLength: BundleProductLengthLike,
  quantityInPieces: number,
  unit: UnitCode
): number | null {
  const unitsPerPiece = unitsPerPieceFor(platformProductsLength, unit);
  return unitsPerPiece == null ? null : quantityInPieces * unitsPerPiece;
}

/**
 * Internal stepper gate: whether a given pieces-per-bundle lets the quantity
 * stepper move past zero. Callers should use `hasBundleDimensions` to block
 * products outright, not this function.
 */
export function canEnterBundleQuantity(
  platformProductsLength: BundleProductLengthLike,
  unit: UnitCode,
  piecesPerBundle: number = platformProductsLength.platformProduct
    .piecesPerBundle.value
): boolean {
  return (
    piecesPerBundle > 0 && hasBundleDimensions(platformProductsLength, unit)
  );
}

/**
 * Whether the product carries the dimensions `unitsPerPiece` needs to convert
 * `unit` to pieces.
 * Unlike a missing bundle size, the buyer cannot supply these on a line, so a
 * caller must block the product outright.
 *
 * @example
 * hasBundleDimensions(item.platformProductsLength, item.quantity.unit)
 */
export function hasBundleDimensions(
  platformProductsLength: BundleProductLengthLike,
  unit: UnitCode
): boolean {
  return unitsPerPieceFor(platformProductsLength, unit) !== null;
}

export function calcUnitsPerBundleForProductLength(
  quantity: Quantity,
  piecesPerBundle: number,
  platformProductsLength: BundleProductLengthLike
): number {
  const unitsPerPiece = unitsPerPieceFor(platformProductsLength, quantity.unit);
  return piecesPerBundle > 0 && unitsPerPiece != null
    ? piecesPerBundle * unitsPerPiece
    : 0;
}

export function unitQuantityForBundleCount(
  bundleCount: number,
  unitsPerBundle: number,
  unit: UnitCode
): Quantity {
  return { value: bundleCount * unitsPerBundle, unit };
}

// Unit values are stored to 3 decimals, so a whole-bundle quantity can sit up
// to 0.0005 above the exact multiple; the tolerance stops that adding a bundle.
const UNIT_VALUE_ROUNDING_TOLERANCE = 0.001;

export function bundleCountForQuantity(
  quantity: Quantity,
  unitsPerBundle: number
): number {
  if (unitsPerBundle <= 0) {
    return 0;
  }
  return Math.max(
    0,
    Math.ceil((quantity.value - UNIT_VALUE_ROUNDING_TOLERANCE) / unitsPerBundle)
  );
}

export function bundleCountForProductLength(
  quantity: Quantity,
  piecesPerBundle: number,
  platformProductsLength: BundleProductLengthLike
): number | null {
  const unitsPerPiece = unitsPerPieceFor(platformProductsLength, quantity.unit);
  if (!(piecesPerBundle > 0) || unitsPerPiece === null) {
    return null;
  }
  const bundleCount = bundleCountForQuantity(
    quantity,
    piecesPerBundle * unitsPerPiece
  );
  return quantity.value > 0 ? Math.max(1, bundleCount) : bundleCount;
}

export function quantityKeepingBundleCount(
  quantity: Quantity,
  fromPiecesPerBundle: number,
  toPiecesPerBundle: number,
  platformProductsLength: BundleProductLengthLike
): Quantity {
  const fromUnitsPerBundle = calcUnitsPerBundleForProductLength(
    quantity,
    fromPiecesPerBundle,
    platformProductsLength
  );
  const toUnitsPerBundle = calcUnitsPerBundleForProductLength(
    quantity,
    toPiecesPerBundle,
    platformProductsLength
  );
  if (fromUnitsPerBundle <= 0 || toUnitsPerBundle <= 0) {
    return quantity;
  }
  return unitQuantityForBundleCount(
    bundleCountForQuantity(quantity, fromUnitsPerBundle),
    toUnitsPerBundle,
    quantity.unit
  );
}

export function wholeBundlePieces(
  quantity: Quantity,
  piecesPerBundle: number,
  platformProductsLength: BundleProductLengthLike
): Quantity {
  const unitsPerBundle = calcUnitsPerBundleForProductLength(
    quantity,
    piecesPerBundle,
    platformProductsLength
  );
  if (unitsPerBundle <= 0) {
    return quantity;
  }
  return {
    value: bundleCountForQuantity(quantity, unitsPerBundle) * piecesPerBundle,
    unit: 'PCS',
  };
}
